/**
 * Admin Maintenance — cleanup tooling.
 * Routes: /admin/maintenance
 *
 * Three cleanup actions plus a live preview of how many items each would
 * touch. Destructive actions sit behind a confirm modal; dry-run mode is
 * default for the broken-images sweep so admins see the impact first.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wrench, CheckCircle2, Image, BarChart3, Loader2, Video,
  AlertTriangle, RefreshCw, PlayCircle, ShieldAlert,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ConfirmModal({ open, title, body, confirmLabel, onCancel, onConfirm, danger }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="glass-card max-w-md w-full p-6" data-testid="cleanup-confirm-modal">
        <div className="flex items-start gap-3 mb-4">
          <ShieldAlert className={`w-6 h-6 mt-0.5 ${danger ? 'text-red-400' : 'text-[#c8b273]'}`} />
          <div>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">{title}</h2>
            <p className="text-[#a8b0a8] text-sm mt-1">{body}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button onClick={onCancel} variant="ghost" data-testid="cleanup-confirm-cancel">Cancel</Button>
          <Button
            onClick={onConfirm}
            className={danger ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-gold'}
            data-testid="cleanup-confirm-go"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMaintenance() {
  const navigate = useNavigate();
  const token = localStorage.getItem('carpcast_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [preview, setPreview] = useState(null);
  const [imgStats, setImgStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // action key currently running
  const [hours, setHours] = useState(24);
  const [confirm, setConfirm] = useState(null); // { title, body, fn, label, danger }

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/admin/cleanup/preview`, auth),
        axios.get(`${API}/admin/image-stats`, auth),
      ]);
      setPreview(p.data);
      setImgStats(s.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admins only');
        navigate('/dashboard', { replace: true });
        return;
      }
      toast.error('Could not load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runApprovePending = async () => {
    setBusy('approve');
    try {
      const r = await axios.post(`${API}/admin/cleanup/approve-old-pending`, { hours: Number(hours) || 24 }, auth);
      toast.success(`Auto-approved ${r.data.modified} pending submission${r.data.modified === 1 ? '' : 's'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setBusy(null); }
  };

  const runRemoveBrokenImages = async (dryRun) => {
    setBusy(dryRun ? 'broken_dry' : 'broken_apply');
    try {
      const r = await axios.post(
        `${API}/admin/cleanup/remove-broken-images`,
        { content_types: ['catch', 'submission'], dry_run: dryRun },
        auth,
      );
      if (dryRun) {
        const totals = Object.entries(r.data.results || {})
          .map(([k, v]) => `${k}: ${v.broken}`).join(' · ');
        toast.message(`Dry run: ${totals || '0 broken'}`);
      } else {
        toast.success(`Soft-hid ${r.data.total_hidden} broken image${r.data.total_hidden === 1 ? '' : 's'}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setBusy(null); }
  };

  const runRebuildStats = async () => {
    setBusy('stats');
    try {
      const r = await axios.post(`${API}/admin/cleanup/rebuild-stats`, {}, auth);
      toast.success(`Recomputed ${r.data.users_touched} users · ${r.data.league_totals_recomputed} league rows`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setBusy(null); }
  };

  const runBackfillLeagueImages = async () => {
    setBusy('backfill');
    try {
      const r = await axios.post(`${API}/admin/cleanup/backfill-league-images`, {}, auth);
      toast.success(`Backfilled ${r.data.updated} league catch image${r.data.updated === 1 ? '' : 's'} (scanned ${r.data.scanned})`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setBusy(null); }
  };

  const runBackfillTutorialVideos = async () => {
    setBusy('videos');
    try {
      const r = await axios.post(`${API}/admin/cleanup/backfill-tutorial-videos`, {}, auth);
      toast.success(`Patched ${r.data.updated} tutorial video${r.data.updated === 1 ? '' : 's'} · ${r.data.lessons_still_without_video} still without video`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setBusy(null); }
  };

  const bytes = (n) => {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(1)} ${u[i]}`;
  };

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12" data-testid="admin-maintenance">
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.label}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const fn = confirm?.fn;
          setConfirm(null);
          if (fn) fn();
        }}
      />

      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#c8b273] mb-1">
              <Wrench className="inline-block w-3 h-3 mr-1.5 -mt-0.5" />
              Admin · Maintenance
            </p>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              Cleanup & diagnostics
            </h1>
          </div>
          <button
            onClick={load}
            className="text-[#8a948a] hover:text-[#c8b273] p-2"
            data-testid="maint-refresh-btn"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading && !preview ? (
          <div className="glass-card p-10 flex items-center justify-center" data-testid="maint-loading">
            <Loader2 className="w-6 h-6 text-[#c8b273] animate-spin" />
          </div>
        ) : (
          <>
            {/* Live preview tiles */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Tile icon={<CheckCircle2 className="w-5 h-5" />} label="Pending > 24h" value={preview?.pending_older_than_24h ?? 0} tone="amber" />
              <Tile icon={<Image className="w-5 h-5" />} label="Broken catches" value={preview?.broken_images?.catch ?? 0} tone="red" />
              <Tile icon={<Image className="w-5 h-5" />} label="Broken submissions" value={preview?.broken_images?.submission ?? 0} tone="red" />
              <Tile icon={<BarChart3 className="w-5 h-5" />} label="Active leagues" value={preview?.active_leagues ?? 0} tone="gold" />
            </div>

            {/* Action cards */}
            <div className="space-y-4">
              <ActionCard
                title="Auto-approve old pending submissions"
                body="Bulk-approve competition submissions still in 'pending' past the threshold. Audited and reversible via the moderation queue."
                action={
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider text-[#8a948a]">Threshold (hours)</span>
                      <Input
                        type="number"
                        min={1}
                        max={720}
                        value={hours}
                        onChange={(e) => setHours(e.target.value)}
                        className="w-24"
                        data-testid="maint-hours-input"
                      />
                    </div>
                    <Button
                      onClick={() => setConfirm({
                        title: 'Approve old pending submissions',
                        body: `This will auto-approve every pending submission older than ${hours}h. Audited and reversible.`,
                        label: 'Approve now',
                        fn: runApprovePending,
                      })}
                      className="btn-gold"
                      disabled={busy === 'approve'}
                      data-testid="maint-approve-pending-btn"
                    >
                      {busy === 'approve' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                      Run
                    </Button>
                  </div>
                }
              />

              <ActionCard
                title="Soft-hide content with broken images"
                body="Sets is_hidden=true on catches/submissions whose image is missing, empty, malformed, or stored on an ephemeral path. Never deletes — restorable from the moderation queue."
                action={
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      onClick={() => runRemoveBrokenImages(true)}
                      variant="outline"
                      disabled={busy === 'broken_dry'}
                      data-testid="maint-broken-dry-btn"
                    >
                      {busy === 'broken_dry' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Preview (dry run)
                    </Button>
                    <Button
                      onClick={() => setConfirm({
                        title: 'Soft-hide broken images',
                        body: 'This will hide every catch/submission whose image is unreadable. They remain in the DB and can be restored from /admin/moderation.',
                        label: 'Hide now',
                        danger: true,
                        fn: () => runRemoveBrokenImages(false),
                      })}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      disabled={busy === 'broken_apply'}
                      data-testid="maint-broken-apply-btn"
                    >
                      {busy === 'broken_apply' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                      Apply
                    </Button>
                  </div>
                }
              />

              <ActionCard
                title="Rebuild user & league stats"
                body="Recomputes catch_count / approved_submissions on every user and recalculates total_points / catch_count on every active-league standings row. Idempotent and read-only at the source."
                action={
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setConfirm({
                        title: 'Rebuild stats',
                        body: 'Recomputes denormalised counters from raw data. Safe to run anytime; idempotent.',
                        label: 'Rebuild now',
                        fn: runRebuildStats,
                      })}
                      className="btn-gold"
                      disabled={busy === 'stats'}
                      data-testid="maint-rebuild-stats-btn"
                    >
                      {busy === 'stats' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                      Rebuild
                    </Button>
                  </div>
                }
              />

              <ActionCard
                title="Backfill League catch images"
                body="Sync durable image_url (base64 / absolute URL) from the mirrored `catches` row onto any `league_catches` row that's still pointing at a wiped ephemeral upload path. Fixes 'No photo' boxes in League Recent Catches without touching anything else. Idempotent."
                action={
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setConfirm({
                        title: 'Backfill League images',
                        body: 'Replaces stale `/api/uploads/...` paths on `league_catches` with the durable image stored on the matching `catches` row. Safe and idempotent.',
                        label: 'Backfill now',
                        fn: runBackfillLeagueImages,
                      })}
                      className="btn-gold"
                      disabled={busy === 'backfill'}
                      data-testid="maint-backfill-league-images-btn"
                    >
                      {busy === 'backfill' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Image className="w-4 h-4 mr-2" />}
                      Backfill
                    </Button>
                  </div>
                }
              />

              <ActionCard
                title="Backfill Learn tutorial videos"
                body="Patch any Learn lesson whose `video_url` is null/missing/empty with a curated YouTube embed. Use this to fix 'Coming Soon' cards on production without re-running the full seed. Never overwrites an existing video URL — fully idempotent."
                action={
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setConfirm({
                        title: 'Backfill tutorial videos',
                        body: 'Fills in missing video_url fields on the tutorials collection using a hand-picked list of public YouTube embeds. Does NOT replace existing videos. Safe and idempotent.',
                        label: 'Backfill now',
                        fn: runBackfillTutorialVideos,
                      })}
                      className="btn-gold"
                      disabled={busy === 'videos'}
                      data-testid="maint-backfill-tutorial-videos-btn"
                    >
                      {busy === 'videos' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Video className="w-4 h-4 mr-2" />}
                      Backfill
                    </Button>
                  </div>
                }
              />
            </div>

            {/* Image storage diagnostics */}
            <div className="mt-10 glass-card p-5" data-testid="image-stats-card">
              <div className="flex items-center gap-2 mb-4">
                <Image className="w-5 h-5 text-[#c8b273]" />
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white">Image storage diagnostics</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Stat label="Backend" value={imgStats?.backend || '—'} />
                <Stat label="Total images" value={imgStats?.total_images ?? 0} />
                <Stat label="DB size (est.)" value={bytes(imgStats?.total_bytes_estimate || 0)} />
                <Stat label="Malformed" value={imgStats?.malformed_count ?? 0} highlight={(imgStats?.malformed_count ?? 0) > 0 ? 'red' : null} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-2">By collection</p>
                  <ul className="space-y-1 text-[#c0c4c0]">
                    {imgStats && Object.entries(imgStats.by_collection || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between border-b border-[#1f221f] py-1.5">
                        <span className="text-[#8a948a]">{k}</span>
                        <span>{v.count} · {bytes(v.bytes_estimate)} · <span className="text-red-400">{v.malformed} broken</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-2">By storage type</p>
                  <ul className="space-y-1 text-[#c0c4c0]">
                    {imgStats && Object.entries(imgStats.by_storage || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between border-b border-[#1f221f] py-1.5">
                        <span className="text-[#8a948a]">{k}</span>
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const TONE = {
  gold:  'border-[#c8b273]/40 text-[#c8b273]',
  amber: 'border-yellow-500/30 text-yellow-300',
  red:   'border-red-500/30 text-red-300',
};

function Tile({ icon, label, value, tone = 'gold' }) {
  return (
    <div className={`glass-card p-4 flex items-center gap-3 border ${TONE[tone]}`} data-testid={`tile-${label.replace(/\W+/g, '-').toLowerCase()}`}>
      <div className="opacity-80">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-[#8a948a] truncate">{label}</p>
        <p className="text-white text-2xl font-bold">{value}</p>
      </div>
    </div>
  );
}

function ActionCard({ title, body, action }) {
  return (
    <div className="glass-card p-5">
      <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-1">{title}</h3>
      <p className="text-sm text-[#a8b0a8] mb-4">{body}</p>
      {action}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="bg-[#0b0d0b] border border-[#1f221f] rounded p-3">
      <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-1">{label}</p>
      <p className={`text-lg font-bold ${highlight === 'red' ? 'text-red-400' : 'text-white'}`}>{value}</p>
    </div>
  );
}
